import React, { Fragment } from 'react';
import moment from 'moment-timezone';
import { I18n, Translate } from 'react-redux-i18n';
import ModernDatepicker from 'react-modern-datepicker';

import AnalyticsServer from '../../../app/server/analytics';
import {
  analyticsTodayDate,
  defaultAnalyticsDateRange,
  formatAnalyticsApiDate,
  onEndDateChange,
  onStartDateChange,
  parseDatepickerChange,
} from '../utils/dateRange';
import {
  AnalyticsBarList,
  AnalyticsFunnel,
  AnalyticsKpiGrid,
  AnalyticsLineChart,
  AnalyticsMetricPills,
  AnalyticsPanel,
  AnalyticsSpark,
  formatKpiValue,
  formatRevenue,
} from './AnalyticsVisuals';
import ReportsTab from './ReportsTab';

const TZ = 'America/Santiago';
const STALE_MS = 48 * 60 * 60 * 1000;
const MAX_DATE = analyticsTodayDate();

const DETAIL_TABS = ['traffic', 'funnel', 'product', 'engagement', 'auth', 'refund'];

const DETAIL_TAB_LABELS = {
  traffic: 'analyticsTabTraffic',
  funnel: 'analyticsTabFunnel',
  product: 'analyticsTabProduct',
  engagement: 'analyticsTabEngagement',
  auth: 'analyticsTabAuth',
  refund: 'analyticsTabRefund',
};

const FUNNEL_STEP_LABELS = {
  view_item: 'analyticsViewItem',
  add_to_cart: 'analyticsFunnelAddToCart',
  begin_checkout: 'analyticsFunnelBeginCheckout',
  purchase: 'analyticsFunnelPurchase',
};

const OVERVIEW_KPIS_COMMERCE = [
  { key: 'activeUsers', label: 'analyticsActiveUsers', format: 'number' },
  { key: 'sessions', label: 'analyticsSessions', format: 'number' },
  { key: 'pageViews', label: 'analyticsPageViews', format: 'number' },
  { key: 'viewItem', label: 'analyticsViewItem', format: 'number' },
  { key: 'addToCart', label: 'analyticsAddToCart', format: 'number' },
  { key: 'beginCheckout', label: 'analyticsBeginCheckout', format: 'number' },
  { key: 'ecommercePurchases', label: 'analyticsPurchases', format: 'number' },
  { key: 'purchaseRevenue', label: 'analyticsPurchaseRevenue', format: 'revenue' },
];

const OVERVIEW_KPIS_COMMERCIAL = [
  { key: 'solicitudNuevosUsuarios', label: 'analyticsSolicitudNuevosUsuarios', format: 'number' },
  { key: 'cantidadCotizaciones', label: 'analyticsCantidadCotizaciones', format: 'number' },
  { key: 'montoCotizaciones', label: 'analyticsMontoCotizaciones', format: 'revenue' },
];

const OVERVIEW_KPIS_ENGAGEMENT = [
  { key: 'generateEstimate', label: 'analyticsGenerateEstimate', format: 'number' },
  { key: 'registrationRequest', label: 'analyticsRegistrationRequest', format: 'number' },
  { key: 'addToWishlist', label: 'analyticsAddToWishlist', format: 'number' },
  { key: 'search', label: 'analyticsSearch', format: 'number' },
  { key: 'contactClick', label: 'analyticsContactClick', format: 'number' },
  { key: 'login', label: 'analyticsLogin', format: 'number' },
  { key: 'signUp', label: 'analyticsSignUp', format: 'number' },
];

const TRAFFIC_METRICS = [
  { value: 'activeUsers', labelKey: 'analyticsActiveUsers' },
  { value: 'sessions', labelKey: 'analyticsSessions' },
  { value: 'addToCart', labelKey: 'analyticsAddToCart' },
  { value: 'beginCheckout', labelKey: 'analyticsBeginCheckout' },
  { value: 'ecommercePurchases', labelKey: 'analyticsPurchases' },
  { value: 'purchaseRevenue', labelKey: 'analyticsPurchaseRevenue' },
];

const AUTH_EVENT_LABELS = {
  login: 'analyticsLogin',
  sign_up: 'analyticsSignUp',
};

function defaultDateRange() {
  return defaultAnalyticsDateRange(6);
}

function formatApiDate(date) {
  return formatAnalyticsApiDate(date);
}

function funnelStepLabel(stepId) {
  const key = FUNNEL_STEP_LABELS[stepId];
  return key ? I18n.t(key) : stepId.replace(/_/g, ' ');
}

function authEventLabel(eventName) {
  const key = AUTH_EVENT_LABELS[eventName];
  return key ? I18n.t(key) : eventName.replace(/_/g, ' ');
}

class HistoryTab extends React.PureComponent {
  constructor(props) {
    super(props);
    const { startDate, endDate } = defaultDateRange();
    this.state = {
      startDate,
      endDate,
      loading: false,
      detailLoading: false,
      overview: null,
      commercialSummary: null,
      lakeStatus: null,
      detailSubTab: 'traffic',
      detailCache: {},
      trafficMetric: 'activeUsers',
      overviewCharts: null,
      chartsLoading: false,
      errorMessage: '',
      showAdvancedReports: false,
    };
  }

  componentDidMount() {
    this.applyRange();
  }

  fetchDetailForTab = async (tab, start, end) => {
    if (tab === 'refund') {
      return { refund: true };
    }

    if (tab === 'engagement') {
      return { engagement: true };
    }

    if (tab === 'traffic') {
      const { trafficMetric } = this.state;
      const [timeseries, topPages] = await Promise.all([
        AnalyticsServer.getHistoryTimeseries(start, end, trafficMetric),
        AnalyticsServer.getHistoryTopPages(start, end),
      ]);
      return { timeseries, topPages };
    }

    if (tab === 'funnel') {
      return AnalyticsServer.getHistoryFunnel(start, end);
    }

    if (tab === 'product') {
      return AnalyticsServer.getHistoryTopItems(start, end);
    }

    if (tab === 'auth') {
      return AnalyticsServer.getHistoryAuthMethods(start, end);
    }

    return null;
  };

  getDetailCacheKey = (tab) => {
    const { startDate, endDate, trafficMetric } = this.state;
    const base = `${formatApiDate(startDate)}:${formatApiDate(endDate)}:${tab}`;
    if (tab === 'traffic') {
      return `${base}:${trafficMetric}`;
    }
    return base;
  };

  loadDetailTab = async (tab, start, end, { showLoading = true } = {}) => {
    const cacheKey = this.getDetailCacheKey(tab);
    const { detailCache } = this.state;

    if (detailCache[cacheKey]) {
      return;
    }

    if (showLoading) {
      this.setState({ detailLoading: true });
    }

    try {
      const data = await this.fetchDetailForTab(tab, start, end);
      this.setState((prev) => ({
        detailLoading: false,
        detailCache: { ...prev.detailCache, [cacheKey]: data },
      }));
    } catch (err) {
      const error = err.response && err.response.data && err.response.data.error;
      this.setState({
        detailLoading: false,
        errorMessage: (error && error.message) || I18n.t('unknownError'),
      });
    }
  };

  loadOverviewCharts = async (start, end) => {
    this.setState({ chartsLoading: true });

    try {
      const [activeUsers, ecommercePurchases, purchaseRevenue] = await Promise.all([
        AnalyticsServer.getHistoryTimeseries(start, end, 'activeUsers'),
        AnalyticsServer.getHistoryTimeseries(start, end, 'ecommercePurchases'),
        AnalyticsServer.getHistoryTimeseries(start, end, 'purchaseRevenue'),
      ]);

      this.setState({
        chartsLoading: false,
        overviewCharts: { activeUsers, ecommercePurchases, purchaseRevenue },
      });
    } catch (err) {
      this.setState({ chartsLoading: false, overviewCharts: null });
    }
  };

  applyRange = async () => {
    const { startDate, endDate, detailSubTab } = this.state;
    const start = formatApiDate(startDate);
    const end = formatApiDate(endDate);

    this.setState({
      loading: true,
      errorMessage: '',
      detailCache: {},
      overviewCharts: null,
    });

    try {
      const [overview, commercialSummary, lakeStatus] = await Promise.all([
        AnalyticsServer.getHistoryOverview(start, end),
        AnalyticsServer.getCommercialSummary(start, end),
        AnalyticsServer.getLakeStatus(),
      ]);

      this.setState({
        overview,
        commercialSummary,
        lakeStatus,
        loading: false,
      }, () => {
        if (!overview || overview.empty) return;
        this.loadOverviewCharts(start, end);
        this.loadDetailTab(detailSubTab, start, end, { showLoading: true });
      });
    } catch (err) {
      const error = err.response && err.response.data && err.response.data.error;
      this.setState({
        loading: false,
        errorMessage: (error && error.message) || I18n.t('unknownError'),
      });
    }
  };

  setDetailSubTab = (tab) => {
    const { startDate, endDate } = this.state;
    const start = formatApiDate(startDate);
    const end = formatApiDate(endDate);

    this.setState({ detailSubTab: tab }, () => {
      const { overview } = this.state;
      if (overview && !overview.empty) {
        this.loadDetailTab(tab, start, end, { showLoading: true });
      }
    });
  };

  setTrafficMetric = (metric) => {
    const { startDate, endDate, overview } = this.state;
    const start = formatApiDate(startDate);
    const end = formatApiDate(endDate);

    this.setState({ trafficMetric: metric }, () => {
      if (overview && !overview.empty) {
        this.loadDetailTab('traffic', start, end, { showLoading: true });
      }
    });
  };

  getCachedDetail = () => {
    const { detailSubTab, detailCache } = this.state;
    const cacheKey = this.getDetailCacheKey(detailSubTab);
    return detailCache[cacheKey];
  };

  isStale = (extractedAt) => {
    if (!extractedAt) return false;
    return (Date.now() - new Date(extractedAt).getTime()) > STALE_MS;
  };

  renderLakeBadge = () => {
    const { lakeStatus, overview } = this.state;
    const badges = [];

    if (overview && overview.source === 'ga4_direct') {
      badges.push(
        <span key="ga4" className="badge badge-info">
          <Translate value="analyticsSourceDirect" />
        </span>,
      );
    }

    if (!lakeStatus) {
      return badges.length ? <Fragment>{badges}</Fragment> : null;
    }

    if (!lakeStatus.enabled || !lakeStatus.configured) {
      badges.push(
        <span key="lake" className="badge badge-secondary">
          <Translate value="analyticsLakeOff" />
        </span>,
      );
    } else {
      if (lakeStatus.pendingCount > 0) {
        badges.push(
          <span key="pending" className="badge badge-warning">
            {I18n.t('analyticsLakePending', { count: lakeStatus.pendingCount })}
          </span>,
        );
      }
      if (lakeStatus.lastSync) {
        badges.push(
          <span key="lake-sync" className="badge badge-success">
            {moment(lakeStatus.lastSync).tz(TZ).format('L LT')}
          </span>,
        );
      }
    }

    if (lakeStatus.lastDbSync) {
      badges.push(
        <span key="db-sync" className="badge badge-light">
          {I18n.t('analyticsDbSync')}
          {': '}
          {moment(lakeStatus.lastDbSync).tz(TZ).format('L LT')}
        </span>,
      );
    }

    return badges.length ? <Fragment>{badges}</Fragment> : null;
  };

  renderKpiSection = (titleKey, kpis, totals) => (
    <div className="analytics-kpi-section">
      <h5 className="analytics-kpi-section__title">
        <Translate value={titleKey} />
      </h5>
      <AnalyticsKpiGrid
        items={kpis.map(({ key, label, format }) => ({
          key,
          labelKey: label,
          value: formatKpiValue(totals[key], format),
        }))}
      />
    </div>
  );

  renderOverviewKpis = (totals) => (
    <Fragment>
      <h4 className="margin-top-20">
        <Translate value="analyticsOverview" />
      </h4>
      {this.renderKpiSection('analyticsKpiCommerce', OVERVIEW_KPIS_COMMERCE, totals)}
      {this.renderKpiSection('analyticsKpiEngagement', OVERVIEW_KPIS_ENGAGEMENT, totals)}
    </Fragment>
  );

  renderOverviewCharts = () => {
    const { overviewCharts, chartsLoading } = this.state;

    if (chartsLoading && !overviewCharts) {
      return (
        <p className="margin-top-20">
          <Translate value="analyticsLoading" />
        </p>
      );
    }

    if (!overviewCharts) return null;

    const { activeUsers, ecommercePurchases, purchaseRevenue } = overviewCharts;

    return (
      <Fragment>
        <h4 className="margin-top-20">
          <Translate value="analyticsChartsTitle" />
        </h4>
        <div className="analytics-charts-row">
          <AnalyticsPanel titleKey="analyticsActiveUsers">
            <AnalyticsLineChart
              points={activeUsers.points}
              color="#348fe2"
            />
          </AnalyticsPanel>
          <AnalyticsPanel titleKey="analyticsPurchases">
            <AnalyticsLineChart
              points={ecommercePurchases.points}
              color="#90ca4b"
            />
          </AnalyticsPanel>
          <AnalyticsPanel titleKey="analyticsPurchaseRevenue">
            <AnalyticsLineChart
              points={purchaseRevenue.points}
              color="#f59c1a"
              formatValue={formatRevenue}
            />
          </AnalyticsPanel>
        </div>
      </Fragment>
    );
  };

  renderOverviewFunnel = (totals) => {
    const steps = [
      { id: 'view_item', value: totals.viewItem || 0 },
      { id: 'add_to_cart', value: totals.addToCart || 0 },
      { id: 'begin_checkout', value: totals.beginCheckout || 0 },
      { id: 'purchase', value: totals.ecommercePurchases || 0 },
    ];

    if (!steps.some((step) => step.value > 0)) return null;

    return (
      <AnalyticsPanel titleKey="analyticsOverviewFunnel">
        <AnalyticsFunnel
          steps={steps}
          formatLabel={(step) => funnelStepLabel(step.id)}
        />
      </AnalyticsPanel>
    );
  };

  renderDetailSubTabs = () => {
    const { detailSubTab } = this.state;

    return (
      <nav className="margin-top-20">
        <div className="nav nav-tabs" role="tablist">
          {DETAIL_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              className={`nav-item nav-link ${detailSubTab === tab ? 'active' : ''}`}
              role="tab"
              aria-selected={detailSubTab === tab}
              onClick={() => this.setDetailSubTab(tab)}
            >
              <Translate value={DETAIL_TAB_LABELS[tab]} />
            </button>
          ))}
        </div>
      </nav>
    );
  };

  renderFunnel = (steps, revenue) => (
    <AnalyticsPanel titleKey="analyticsFunnel">
      <AnalyticsFunnel
        steps={steps}
        formatLabel={(step) => funnelStepLabel(step.id)}
      />
      {revenue != null && (
        <p className="margin-top-20 margin-bottom-0">
          <Translate value="analyticsPurchaseRevenue" />
          {': '}
          <strong>{formatRevenue(revenue)}</strong>
        </p>
      )}
    </AnalyticsPanel>
  );

  renderTimeseries = (timeseries) => {
    const { trafficMetric } = this.state;

    if (!timeseries || !timeseries.points || !timeseries.points.length) {
      return (
        <div className="analytics-empty">
          <p>
            <Translate value="analyticsEmpty" />
          </p>
        </div>
      );
    }

    const isRevenue = trafficMetric === 'purchaseRevenue';

    return (
      <AnalyticsPanel titleKey="analyticsTimeseries">
        <AnalyticsMetricPills
          options={TRAFFIC_METRICS}
          value={trafficMetric}
          onChange={this.setTrafficMetric}
        />
        <AnalyticsLineChart
          points={timeseries.points}
          color="#348fe2"
          formatValue={isRevenue ? formatRevenue : undefined}
        />
        <div className="margin-top-20">
          <AnalyticsSpark points={timeseries.points} />
        </div>
      </AnalyticsPanel>
    );
  };

  renderTopPages = (pages) => {
    if (!pages || !pages.length) {
      return null;
    }

    return (
      <AnalyticsPanel titleKey="analyticsTopPages">
        <AnalyticsBarList
          rows={pages.map((row) => ({
            key: row.pagePath,
            label: row.pagePath,
            value: row.screenPageViews,
          }))}
          tone="teal"
        />
      </AnalyticsPanel>
    );
  };

  renderTopItems = (rows) => {
    if (!rows || !rows.length) {
      return (
        <div className="analytics-empty">
          <p>
            <Translate value="analyticsEmpty" />
          </p>
        </div>
      );
    }

    return (
      <AnalyticsPanel titleKey="analyticsTopItems">
        <AnalyticsBarList
          rows={rows.map((row) => ({
            key: row.itemId,
            label: row.itemName || row.itemId,
            value: row.purchaseCount || row.viewItemCount || 0,
          }))}
          tone="orange"
        />
        <table className="table table-striped margin-top-20">
          <thead className="thead-dark">
            <tr>
              <th scope="col">
                <Translate value="analyticsItem" />
              </th>
              <th scope="col">
                <Translate value="analyticsViewItem" />
              </th>
              <th scope="col">
                <Translate value="analyticsAddToCart" />
              </th>
              <th scope="col">
                <Translate value="analyticsPurchases" />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.itemId}>
                <td>{row.itemName || row.itemId}</td>
                <td>{row.viewItemCount}</td>
                <td>{row.addToCartCount}</td>
                <td>{row.purchaseCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </AnalyticsPanel>
    );
  };

  renderAuthMethods = (rows) => {
    if (!rows || !rows.length) {
      return (
        <div className="analytics-empty">
          <p>
            <Translate value="analyticsEmpty" />
          </p>
        </div>
      );
    }

    return (
      <AnalyticsPanel titleKey="analyticsAuthMethods">
        <AnalyticsBarList
          rows={rows.map((row) => ({
            key: `${row.eventName}-${row.method}`,
            label: `${authEventLabel(row.eventName)} · ${row.method}`,
            value: row.eventCount,
          }))}
        />
      </AnalyticsPanel>
    );
  };

  renderEngagementDetail = (totals) => {
    const rows = [
      { key: 'generateEstimate', labelKey: 'analyticsGenerateEstimate', value: totals.generateEstimate || 0 },
      { key: 'registrationRequest', labelKey: 'analyticsRegistrationRequest', value: totals.registrationRequest || 0 },
      { key: 'addToWishlist', labelKey: 'analyticsAddToWishlist', value: totals.addToWishlist || 0 },
      { key: 'search', labelKey: 'analyticsSearch', value: totals.search || 0 },
      { key: 'contactClick', labelKey: 'analyticsContactClick', value: totals.contactClick || 0 },
    ];

    return (
      <div className="margin-top-20">
        <AnalyticsKpiGrid
          items={[
            {
              key: 'generateEstimate',
              labelKey: 'analyticsGenerateEstimate',
              value: totals.generateEstimate || 0,
              tone: 'purple',
            },
            {
              key: 'registrationRequest',
              labelKey: 'analyticsRegistrationRequest',
              value: totals.registrationRequest || 0,
              tone: 'indigo',
            },
            {
              key: 'addToWishlist',
              labelKey: 'analyticsAddToWishlist',
              value: totals.addToWishlist || 0,
              tone: 'teal',
            },
            {
              key: 'search',
              labelKey: 'analyticsSearch',
              value: totals.search || 0,
              tone: 'blue',
            },
            {
              key: 'contactClick',
              labelKey: 'analyticsContactClick',
              value: totals.contactClick || 0,
              tone: 'orange',
            },
          ]}
        />
        <AnalyticsPanel titleKey="analyticsTabEngagement">
          <AnalyticsBarList
            rows={rows.map((row) => ({
              key: row.key,
              label: I18n.t(row.labelKey),
              value: row.value,
            }))}
            tone="purple"
          />
        </AnalyticsPanel>
      </div>
    );
  };

  renderAdvancedReports = () => {
    const { showAdvancedReports } = this.state;

    return (
      <div className="analytics-advanced margin-top-30">
        <button
          type="button"
          className="btn btn-link analytics-advanced__toggle"
          onClick={() => this.setState({ showAdvancedReports: !showAdvancedReports })}
        >
          <Translate value={showAdvancedReports ? 'analyticsAdvancedHide' : 'analyticsAdvancedShow'} />
        </button>
        {showAdvancedReports && (
          <div className="analytics-advanced__panel margin-top-10">
            <ReportsTab />
          </div>
        )}
      </div>
    );
  };

  renderRefundDetail = (totals) => (
    <div className="margin-top-20">
      <AnalyticsKpiGrid
        items={[
          {
            key: 'refunds',
            labelKey: 'analyticsRefunds',
            value: totals.refunds,
            tone: 'red',
          },
          {
            key: 'refundRevenue',
            labelKey: 'analyticsRefundRevenue',
            value: formatRevenue(totals.refundRevenue),
            tone: 'orange',
          },
        ]}
      />
    </div>
  );

  renderDetailContent = () => {
    const { detailSubTab, detailLoading, overview } = this.state;
    const detail = this.getCachedDetail();

    if (detailLoading && !detail) {
      return (
        <p className="margin-top-20">
          <Translate value="analyticsLoading" />
        </p>
      );
    }

    if (detailSubTab === 'traffic' && detail) {
      return (
        <Fragment>
          {this.renderTimeseries(detail.timeseries)}
          {detail.topPages && detail.topPages.pages
            && this.renderTopPages(detail.topPages.pages)}
        </Fragment>
      );
    }

    if (detailSubTab === 'funnel' && detail && detail.steps) {
      return this.renderFunnel(detail.steps, detail.revenue);
    }

    if (detailSubTab === 'product' && detail) {
      return this.renderTopItems(detail.rows);
    }

    if (detailSubTab === 'auth' && detail) {
      return this.renderAuthMethods(detail.rows);
    }

    if (detailSubTab === 'engagement' && overview && overview.totals) {
      return this.renderEngagementDetail(overview.totals);
    }

    if (detailSubTab === 'refund' && overview && overview.totals) {
      return this.renderRefundDetail(overview.totals);
    }

    return null;
  };

  renderEmpty = () => (
    <div className="analytics-empty">
      <p>
        <Translate value="analyticsEmpty" />
      </p>
      <p>
        <Translate value="analyticsSeeLiveForNow" />
      </p>
    </div>
  );

  render() {
    const {
      startDate,
      endDate,
      loading,
      overview,
      commercialSummary,
      errorMessage,
    } = this.state;

    const isEmpty = overview && overview.empty;
    const extractedAt = overview && overview.extractedAt;
    const showStale = this.isStale(extractedAt);

    return (
      <div className="analytics-page margin-top-20">
        <div className="row">
          <div className="col-lg-6">
            <div className="form-group form-promotion-picker">
              <p className="margin-bottom-2">{I18n.t('initialDate')}</p>
              <div className="inputs">
                <ModernDatepicker
                  className="form-control"
                  showBorder
                  placeholder=""
                  format="DD/MM/YYYY"
                  date={startDate}
                  maxDate={MAX_DATE}
                  onChange={(date) => {
                    const nextStart = parseDatepickerChange(date);
                    const { startDate: s, endDate: e } = onStartDateChange(nextStart, endDate);
                    this.setState({ startDate: s, endDate: e });
                  }}
                />
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="form-group form-promotion-picker">
              <p className="margin-bottom-2">{I18n.t('finalDate')}</p>
              <div className="inputs">
                <ModernDatepicker
                  className="form-control"
                  showBorder
                  placeholder=""
                  format="DD/MM/YYYY"
                  date={endDate}
                  maxDate={MAX_DATE}
                  onChange={(date) => {
                    const nextEnd = parseDatepickerChange(date);
                    const { startDate: s, endDate: e } = onEndDateChange(startDate, nextEnd);
                    this.setState({ startDate: s, endDate: e });
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="btn-group" role="group" aria-label={I18n.t('generateReport')}>
          <button
            className="btn btn-primary"
            type="button"
            disabled={loading}
            onClick={this.applyRange}
          >
            {I18n.t('generateReport')}
          </button>
        </div>

        {loading && !overview && (
          <p className="margin-top-20">
            <Translate value="analyticsLoading" />
          </p>
        )}

        {errorMessage && (
          <p className="color-red margin-top-20">{errorMessage}</p>
        )}

        {showStale && (
          <div className="alert alert-warning margin-top-20" role="alert">
            <Translate value="analyticsStale" />
          </div>
        )}

        {extractedAt && !isEmpty && (
          <div className="analytics-meta">
            <span>
              {I18n.t('analyticsUpdatedAt')}
              {': '}
              {moment(extractedAt).tz(TZ).format('L LT')}
            </span>
            {this.renderLakeBadge()}
          </div>
        )}

        {isEmpty && !commercialSummary && (
          <Fragment>
            {this.renderEmpty()}
            {this.state.lakeStatus && (
              <div className="analytics-meta">
                {this.renderLakeBadge()}
              </div>
            )}
          </Fragment>
        )}

        {commercialSummary && (
          <Fragment>
            {this.renderKpiSection(
              'analyticsKpiCommercial',
              OVERVIEW_KPIS_COMMERCIAL,
              commercialSummary,
            )}
          </Fragment>
        )}

        {!isEmpty && overview && overview.totals && (
          <Fragment>
            {this.renderOverviewKpis(overview.totals)}
            {this.renderOverviewCharts()}
            {this.renderOverviewFunnel(overview.totals)}
            {this.renderDetailSubTabs()}
            {this.renderDetailContent()}
            {this.renderAdvancedReports()}
          </Fragment>
        )}
      </div>
    );
  }
}

export default HistoryTab;
